import { loadAuthorized, loadByKind, create, update, deleteByKind, list } from "common/api/token.service";
import { addFlashAlert } from "common/components/FlashAlert";
import { QueryParamsRecord } from "common/records/QueryParams";
import { ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { TokenActions } from "common/reducers/token.actions";
import { RootDispatchType, RootGetState } from "common/store";
import { Nullable } from "common/types";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { integrationsData } from "orgs/Configuration/IntegrationsTab/integrationsData";
import { authorizedToken, currentOrgAuthorizedToken, token } from "common/selectors/token.selectors";
import { List } from "immutable";
import { TokenKind } from "common/records/Token";
import { currentOrgId } from "common/selectors/org.selectors";
import { userAclCheck } from "common/selectors/user.selectors";

export const TokenAsyncActions = {
  getToken: (orgId: number, tokenKind: TokenKind, parentTokenLoad = false): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(TokenActions.fetchToken(orgId, tokenKind));
      try {
        const result = await loadByKind(orgId, tokenKind, parentTokenLoad);
        if (result.token) {
          dispatch(TokenActions.receiveToken(orgId, result.token));
        } else {
          // Setup default for redux store if token does not currently exist on the org
          dispatch(TokenActions.receiveToken(orgId, { org_id: orgId, kind: tokenKind }));
        }
      } catch (response) {
        handleErrors(response, true);
        dispatch(TokenActions.setLoaded(orgId, tokenKind));
      }
    };
  },
  getAuthorizedTokens: (orgId: Nullable<number>): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      if (!orgId) {
        addFlashAlert("danger", "No organization selected");
        return;
      }
      dispatch(TokenActions.fetchAuthorized(orgId));
      try {
        const result = await loadAuthorized(orgId);
        dispatch(TokenActions.receiveAuthorized(orgId, result));
      } catch (response) {
        handleErrors(response);
        dispatch(TokenActions.setAuthorizedLoaded(orgId));
      }
    };
  },
  updateCreateToken: (kind: Nullable<TokenKind>, throwError = false): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      if (!kind) {
        addFlashAlert("danger", "Invalid token kind");
        if (throwError) throw "Invalid token kind";
        return;
      }

      let tokenRecord = token(getState(), { kind });

      if (!tokenRecord) {
        addFlashAlert("danger", "No token found");
        if (throwError) throw "No token found";
        return;
      }

      const orgId = currentOrgId(getState());

      if (!orgId) {
        addFlashAlert("danger", "No org found");
        if (throwError) throw "No org found";
        return;
      }

      dispatch(TokenActions.fetchToken(orgId, kind));
      try {
        if (tokenRecord.id > 0) {
          const result = await update(tokenRecord);
          dispatch(TokenActions.receiveToken(orgId, result.token));
          addFlashAlert("success", "Successfully updated");
        } else {
          tokenRecord = tokenRecord.set("org_id", orgId);
          const result = await create(tokenRecord);
          dispatch(TokenActions.receiveToken(orgId, result.token));
          addFlashAlert("success", "Successfully created");
        }
      } catch (response) {
        dispatch(TokenActions.setLoaded(orgId, kind));
        if (throwError) {
          throw response;
        } else {
          handleErrors(response);
        }
      }
    };
  },
  deleteToken: (tokenKind: TokenKind): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) {
        addFlashAlert("danger", "No organization selected");
        return;
      }

      dispatch(TokenActions.fetchToken(orgId, tokenKind));
      try {
        await deleteByKind(orgId, tokenKind);
        dispatch(TokenActions.removeToken(orgId, tokenKind));
        addFlashAlert("success", "Authorization Rejected");
      } catch (response) {
        handleErrors(response);
        dispatch(TokenActions.setLoaded(orgId, tokenKind));
      }
    };
  },
  listAuthorizedIntegrations: (
    orgId: number,
    modelName: string,
    queryParams: QueryParamsRecord,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        dispatch(IdsManagementActions.fetchModel(modelName, queryParams));

        // Not hitting the API, filtering hardcoded integrations
        const ints = List(Object.keys(integrationsData) as TokenKind[])
          .filter((kind) => integrationsData[kind]?.name.toLowerCase().includes(queryParams.query?.toLowerCase() || ""))
          .filter((kind) => {
            if (queryParams.parameters.get("status") === "connected") {
              const authorization = authorizedToken(getState(), { orgId, kind });
              return authorization?.id && authorization?.id > 0;
            } else {
              return true;
            }
          })
          .filter((kind) => {
            const resource = integrationsData[kind]?.resource;
            const permission = integrationsData[kind]?.permission;
            if (resource && permission) {
              return userAclCheck(getState(), { resource, permission });
            }
            return true;
          })
          .filter((kind) => {
            const tab = queryParams.parameters.get("tab");
            if (tab && tab !== "all") {
              return integrationsData[kind]?.category === queryParams.parameters.get("tab");
            }
            return true;
          });

        dispatch(IdsManagementActions.receiveModel(modelName, ints));
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.receiveModel(modelName, List()));
      }
    };
  },
  listTokens: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        const orgId = currentOrgId(getState());
        const result = await list(orgId as number);

        if (result.tokens) {
          dispatch(TokenActions.receiveTokens(orgId as number, result.tokens));
        }

        if (orgId) {
          const sfAuthorized = currentOrgAuthorizedToken(getState(), { kind: "salesforce" })?.authorized ?? false;
          if (sfAuthorized) {
            dispatch(TokenAsyncActions.getToken(orgId, "salesforce", true));
          }
        }
      } catch (response) {
        handleErrors(response, true);
      }
    };
  },
};
